import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { ChevronDown, Users } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * "About this team" — compact, expandable TEAM CONTEXT card at the top of the
 * Team page. Answers the shared-context questions: which team am I viewing,
 * what is my role, who is on the team, and what is shared vs private.
 *
 * SHARED CONTEXT ONLY. The roster is identity + role. It carries NO Priority
 * Center activity — no relevance marks, linked work, personal priorities,
 * status, importance, counts, "last active", or progress. Private Priority
 * Center data stays private (see server/teamRoster.ts, the narrow member-gated
 * read this card consumes).
 *
 * No numeric member count anywhere (label "Members", never "8 members").
 * No manage-members affordance in v1: there is no currently-reachable manage
 * surface, and building an invite/management flow is out of scope for this
 * pass.
 */
type TeamRole = "owner" | "manager" | "lead" | "member";

const roleLabel = (role: TeamRole, isAr: boolean) =>
  ({
    owner: isAr ? "المالك" : "Owner",
    manager: isAr ? "مدير" : "Manager",
    lead: isAr ? "قائد" : "Lead",
    member: isAr ? "عضو" : "Member",
  })[role] ?? role;

export function TeamContextCard({
  teamId,
  teamName,
  role,
  isAr,
}: {
  teamId: number;
  /** Already available from the active workspace — NOT a fetch. */
  teamName: string;
  /** The current user's OWN team role — from the active workspace. */
  role: TeamRole;
  isAr: boolean;
}) {
  const t = (en: string, ar: string) => (isAr ? ar : en);
  const [expanded, setExpanded] = useState(false);
  // LAZY: the roster is only fetched once the card is expanded — the collapsed
  // face (team name + your role) needs no network at all.
  const roster = trpc.team.roster.useQuery({ teamId }, { enabled: expanded });
  const members = roster.data ?? [];

  return (
    <section
      data-testid="team-context-card"
      className="rounded-3xl border border-border/60 bg-card p-5 shadow-sm"
    >
      {/* Collapsed face: team name + your role. Both come from the active
          workspace — no fetch, so this renders instantly. */}
      <button
        type="button"
        data-testid="team-context-toggle"
        onClick={() => setExpanded(e => !e)}
        aria-expanded={expanded}
        className="flex w-full items-center justify-between gap-3 text-start"
      >
        <span className="min-w-0">
          <span
            data-testid="team-context-eyebrow"
            className="block text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/80"
          >
            {t("About this team", "عن هذا الفريق")}
          </span>
          <span className="mt-0.5 flex items-center gap-2 min-w-0">
            <Users className="h-4 w-4 text-coral shrink-0" />
            <span
              data-testid="team-context-name"
              className="font-display text-base truncate"
            >
              {teamName}
            </span>
          </span>
          <span
            data-testid="team-context-role"
            className="mt-0.5 block text-[11px] text-muted-foreground"
          >
            {t("Your role", "دورك")}: {roleLabel(role, isAr)}
          </span>
        </span>
        <ChevronDown
          className={cn(
            "h-4 w-4 text-muted-foreground shrink-0 transition-transform",
            expanded && "rotate-180",
          )}
        />
      </button>

      {expanded ? (
        <div className="mt-3 space-y-3" data-testid="team-context-panel">
          {/* Members — identity + role only. No numeric count ("Members",
              never "N members"). */}
          <div>
            <div
              data-testid="team-context-members-label"
              className="text-[10px] font-semibold tracking-wide text-muted-foreground/80"
            >
              {t("Members", "الأعضاء")}
            </div>
            <ul className="mt-1 space-y-1">
              {members.map(m => (
                <li
                  key={m.id}
                  data-testid={`roster-${m.id}`}
                  className="flex items-center justify-between gap-3 rounded-xl px-2 py-1.5 hover:bg-muted/40"
                >
                  <span className="min-w-0 truncate text-sm">
                    {m.name ?? t("Teammate", "زميل")}
                  </span>
                  <span
                    data-testid={`roster-role-${m.id}`}
                    className="shrink-0 text-[11px] text-muted-foreground"
                  >
                    {roleLabel(m.role as TeamRole, isAr)}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {/* LOAD-BEARING PRIVACY COPY. This states exactly what is shared with
              the team vs private to the user. If a future feature changes what
              is shared or private, THIS COPY MUST CHANGE IN THE SAME COMMIT as
              that model change. Sentence style, no dash separators. */}
          <p
            data-testid="team-context-trust"
            className="text-[11px] leading-relaxed text-muted-foreground"
          >
            {t(
              "Team Direction is shared with this team. My priorities, relevance marks, and linked work are private to you. Owners, managers, and leads can edit Team Direction.",
              "توجيه الفريق مشترك مع هذا الفريق. أولوياتي وعلامات الصلة والعمل المرتبط خاصة بك. يمكن للمالكين والمديرين والقادة تعديل توجيه الفريق.",
            )}
          </p>
        </div>
      ) : null}
    </section>
  );
}
